/**
 * native.ts
 *
 * Centralises all Capacitor plugin calls behind a single service.
 * Every call is guarded with isNative() so the web build never
 * tries to invoke native APIs that don't exist in the browser.
 */
import { Capacitor } from '@capacitor/core'

// ── Platform detection ────────────────────────────────────────────────────────

export const isNative  = ()  => Capacitor.isNativePlatform()
export const isIOS     = ()  => Capacitor.getPlatform() === 'ios'
export const isAndroid = ()  => Capacitor.getPlatform() === 'android'
export const isWeb     = ()  => Capacitor.getPlatform() === 'web'

// ── Camera ────────────────────────────────────────────────────────────────────

export type CameraSource = 'camera' | 'photos'

export interface CapturedPhoto {
  dataUrl:   string     // base64 data URL — drop-in for <img src>
  mimeType:  string
  file?:     File       // available on web, null on native
}

export async function captureTicketPhoto(source: CameraSource = 'camera'): Promise<CapturedPhoto | null> {
  if (!isNative()) {
    // Web fallback — open file input
    return capturePhotoWeb()
  }

  const { Camera, CameraResultType, CameraSource: CapCameraSource } = await import('@capacitor/camera')

  try {
    const photo = await Camera.getPhoto({
      quality:      90,
      allowEditing: false,
      resultType:   CameraResultType.DataUrl,
      source:       source === 'camera' ? CapCameraSource.Camera : CapCameraSource.Photos,
      width:        1280,
      height:       1280,
      correctOrientation: true,
    })

    return {
      dataUrl:  photo.dataUrl ?? '',
      mimeType: `image/${photo.format}`,
    }
  } catch (err: any) {
    // User cancelled — not an error
    if (err?.message?.includes('cancelled') || err?.message?.includes('canceled')) return null
    throw err
  }
}

function capturePhotoWeb(): Promise<CapturedPhoto | null> {
  return new Promise(resolve => {
    const input = document.createElement('input')
    input.type   = 'file'
    input.accept = 'image/jpeg,image/png,image/heic,image/webp'
    input.onchange = () => {
      const file = input.files?.[0]
      if (!file) { resolve(null); return }
      const reader = new FileReader()
      reader.onload  = () => resolve({ dataUrl: reader.result as string, mimeType: file.type, file })
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(file)
    }
    input.oncancel = () => resolve(null)
    input.click()
  })
}

// ── Convert native photo to File for API upload ───────────────────────────────

export function dataUrlToFile(dataUrl: string, filename: string, mimeType: string): File {
  const arr  = dataUrl.split(',')
  const bstr = atob(arr[1])
  const u8   = new Uint8Array(bstr.length)
  for (let i = 0; i < bstr.length; i++) u8[i] = bstr.charCodeAt(i)
  return new File([u8], filename, { type: mimeType })
}

// ── Haptics ───────────────────────────────────────────────────────────────────

export async function hapticSuccess() {
  if (!isNative()) return
  const { Haptics, ImpactStyle } = await import('@capacitor/haptics')
  await Haptics.impact({ style: ImpactStyle.Medium })
}

export async function hapticLight() {
  if (!isNative()) return
  const { Haptics, ImpactStyle } = await import('@capacitor/haptics')
  await Haptics.impact({ style: ImpactStyle.Light })
}

export async function hapticNotification(type: 'success' | 'warning' | 'error' = 'success') {
  if (!isNative()) return
  const { Haptics, NotificationType } = await import('@capacitor/haptics')
  const map = {
    success: NotificationType.Success,
    warning: NotificationType.Warning,
    error:   NotificationType.Error,
  }
  await Haptics.notification({ type: map[type] })
}

// ── Status bar ────────────────────────────────────────────────────────────────

export async function setStatusBarDark() {
  if (!isNative()) return
  const { StatusBar, Style } = await import('@capacitor/status-bar')
  await StatusBar.setStyle({ style: Style.Dark })
  if (isAndroid()) await StatusBar.setBackgroundColor({ color: '#0F1923' })
}

export async function setStatusBarLight() {
  if (!isNative()) return
  const { StatusBar, Style } = await import('@capacitor/status-bar')
  await StatusBar.setStyle({ style: Style.Light })
  if (isAndroid()) await StatusBar.setBackgroundColor({ color: '#FFFFFF' })
}

// ── Secure storage (tokens) ───────────────────────────────────────────────────

export async function secureSet(key: string, value: string): Promise<void> {
  if (!isNative()) { localStorage.setItem(key, value); return }
  const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin')
  await SecureStoragePlugin.set({ key, value })
}

export async function secureGet(key: string): Promise<string | null> {
  if (!isNative()) return localStorage.getItem(key)
  try {
    const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin')
    const result = await SecureStoragePlugin.get({ key })
    return result.value
  } catch {
    return null
  }
}

export async function secureRemove(key: string): Promise<void> {
  if (!isNative()) { localStorage.removeItem(key); return }
  try {
    const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin')
    await SecureStoragePlugin.remove({ key })
  } catch {}
}

// ── App lifecycle ─────────────────────────────────────────────────────────────

export async function initAppListeners(onResume: () => void) {
  if (!isNative()) return
  const { App } = await import('@capacitor/app')
  await App.addListener('appStateChange', ({ isActive }) => {
    if (isActive) onResume()
  })
}

export async function exitApp() {
  if (!isNative()) return
  const { App } = await import('@capacitor/app')
  await App.exitApp()
}

// ── Permissions ───────────────────────────────────────────────────────────────
// Request camera + photo library permissions upfront on Android 13+
// Called once after login so permissions are ready before the user needs them.
export async function requestMediaPermissions(): Promise<void> {
  if (!isNative()) return
  try {
    const { Camera } = await import('@capacitor/camera')
    const status = await Camera.checkPermissions()
    if (status.camera === 'denied' || status.photos === 'denied') return
    if (status.camera !== 'granted' || status.photos !== 'granted') {
      await Camera.requestPermissions({
        permissions: ['camera', 'photos'],
      })
    }
  } catch {
    // Permissions API not available on this platform — ignore
  }
}
